// contexts/StorageStateContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface StorageState {
  usedBytes: number;
  limitBytes: number | typeof Infinity;
  remainingBytes: number | typeof Infinity;
  percentage: number;
  readOnly: boolean;
  almostFull: boolean;
}

interface StorageStateContextType {
  storageState: StorageState | null;
  setStorageState: (state: StorageState | null) => void;
}

const StorageStateContext = createContext<StorageStateContextType | undefined>(undefined);

export function StorageStateProvider({ children }: { children: ReactNode }) {
  const [storageState, setStorageState] = useState<StorageState | null>(null);

  // Listen for storageState updates from API responses
  useEffect(() => {
    const handleStorageStateUpdate = (event: CustomEvent<StorageState>) => {
      setStorageState(event.detail);
    };

    window.addEventListener('storageStateUpdate', handleStorageStateUpdate as EventListener);

    return () => {
      window.removeEventListener('storageStateUpdate', handleStorageStateUpdate as EventListener);
    };
  }, []);

  return (
    <StorageStateContext.Provider value={{ storageState, setStorageState }}>
      {children}
    </StorageStateContext.Provider>
  );
}

export function useStorageState() {
  const context = useContext(StorageStateContext);
  if (context === undefined) {
    throw new Error('useStorageState must be used within a StorageStateProvider');
  }
  return context;
}

